import { useTranslation } from 'react-i18next';
import { TalkToListenRatioData } from '../../../../../types/live-insights';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface Props {
    data: TalkToListenRatioData;
    pieChart?: boolean;
}

export const TalkToListenMeter: React.FC<Props> = ({ data, pieChart = false }) => {
    const { t } = useTranslation();

    if (pieChart) {
        const series = [data.therapist_ratio * 100, data.patient_ratio * 100];
        const options: ApexOptions = {
            chart: {
                type: 'donut',
                fontFamily: 'inherit',
                animations: {
                    enabled: true,
                    speed: 800,
                }
            },
            labels: [t('settings.liveDashboard.therapist'), t('settings.liveDashboard.patient')],
            colors: [data.warning ? '#fbbf24' : '#34d399', '#818cf8'],
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent']
            },
            dataLabels: {
                enabled: false,
            },
            legend: {
                position: 'bottom',
                fontSize: '11px',
                fontWeight: 500,
                markers: {
                    //@ts-expect-error - ApexCharts types are missing radius in markers
                    radius: 4,
                    offsetY: 1
                },
                itemMargin: {
                    horizontal: 10,
                    vertical: 5
                }
            },
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    donut: {
                        size: '82%',
                        labels: {
                            show: true,
                            name: {
                                show: true,
                                fontSize: '11px',
                                fontWeight: 500,
                                color: '#94a3b8',
                                offsetY: 20
                            },
                            value: {
                                show: true,
                                fontSize: '28px',
                                fontWeight: 300,
                                color: '#1e293b',
                                offsetY: -15,
                                formatter: (val) => `${Math.round(Number(val))}%`
                            },
                            total: {
                                show: true,
                                label: t('settings.liveDashboard.therapist'),
                                color: '#94a3b8',
                                formatter: function (w) {
                                    return Math.round(Number(w.globals.series[0])) + "%";
                                }
                            }
                        }
                    }
                }
            },
            tooltip: {
                enabled: true,
                theme: 'light',
                style: {
                    fontSize: '11px'
                },
                y: {
                    formatter: (val) => `${Math.round(val)}%`
                }
            }
        };

        return (
            <div className="p-6 bg-white/40 rounded-2xl shadow-sm ring-1 ring-black/5 backdrop-blur-md flex flex-col items-center">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">
                    {t('settings.liveDashboard.talkToListenRatio')}
                </h3>
                <div className="w-full max-w-[320px]">
                    <Chart options={options} series={series} type="donut" width="100%" />
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 bg-white rounded-lg shadow border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">{t('settings.liveDashboard.talkToListenRatio')}</h3>
            <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-slate-500 w-16">{t('settings.liveDashboard.therapist')}</span>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full ${data.warning ? 'bg-amber-500' : 'bg-emerald-500'} transition-all duration-300`}
                        style={{ width: `${Math.max(5, data.therapist_ratio * 100)}%` }}
                    />
                </div>
                <span className="text-xs font-medium text-slate-700 w-8">{Math.round(data.therapist_ratio * 100)}%</span>
            </div>
            <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-16">{t('settings.liveDashboard.patient')}</span>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-accent transition-all duration-300"
                        style={{ width: `${Math.max(5, data.patient_ratio * 100)}%` }}
                    />
                </div>
                <span className="text-xs font-medium text-slate-700 w-8">{Math.round(data.patient_ratio * 100)}%</span>
            </div>
        </div>
    );
};
